import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';

import {
  AllowedTagNamesFragment,
  ApprovedTagsCard,
} from './approvedTagsCard/ApprovedTagsCard';
import {
  DefaultIgnoreLinterSettingsFragment,
  DefaultLinterSettingsCard,
} from './defaultLinterSettingsCard/DefaultLinterSettingsCard';
import {
  LinterRulesCard,
  LinterRulesCardFragment,
} from './linterRulesCard/LinterRulesCard';

// NOTE: Linter configuration is only managable at the graph level. Though this
// page takes into account its appearance on as a variant level config, it is strictly
// read only.
export const LinterConfigPage = ({
  graphRef,
  type,
}: {
  graphRef: GraphRef;
  type: 'graph' | 'variant';
}) => {
  const { data, loading } = useQuery<
    GraphQLTypes.LinterConfigPageQuery,
    GraphQLTypes.LinterConfigPageQueryVariables
  >(
    gql`
      query LinterConfigPageQuery($graphId: ID!, $variantName: String!) {
        service(id: $graphId) {
          id
          account {
            id
            name
          }
          variant(name: $variantName) {
            id
            hasSupergraphSchema
          }
          variants {
            id
            name
            hasManagedSubgraphs
            buildConfig {
              buildPipelineTrack
            }
          }
          title
          checkConfiguration {
            id
            enableLintChecks
          }
          linterConfiguration {
            ...DefaultIgnoreLinterSettingsFragment
            ...LinterRulesCardFragment
            ...AllowedTagNamesFragment
          }
        }
      }
      ${DefaultIgnoreLinterSettingsFragment}
      ${AllowedTagNamesFragment}
      ${LinterRulesCardFragment}
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        variantName: graphRef.graphVariant,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const graph = data?.service;
  const variant = graph?.variant;
  const checkConfiguration = graph?.checkConfiguration;

  if (loading) {
    return <Loading />;
  } else if (!graph || !checkConfiguration || !variant) {
    return <NotFound />;
  }

  const linterConfiguration = graph.linterConfiguration;

  return (
    <>
      <DefaultLinterSettingsCard
        graphRef={graphRef}
        isVariantConfig={type === 'variant'}
        checkConfig={checkConfiguration}
        linterConfig={linterConfiguration}
        graphTitle={data?.service?.title ?? graphRef.graphId}
      />
      <ApprovedTagsCard
        graphRef={graphRef}
        isVariantConfig={type === 'variant'}
        linterConfig={linterConfiguration}
      />
      <LinterRulesCard
        graphRef={graphRef}
        isVariantConfig={type === 'variant'}
        isFederated={variant.hasSupergraphSchema}
        allVariants={data?.service?.variants ?? []}
        linterConfig={linterConfiguration}
      />
    </>
  );
};
